import { useState, useCallback, useEffect } from 'react'
import config from '@config'

const {
  video: { videoMetadataUrl },
} = config

interface VideoSource {
  src: string
}
interface VideoMetadata {
  id: string
  name?: string
  poster?: string
  thumbnail?: string
  sources?: VideoSource[]
}

export type VideoMetadataResponse = VideoMetadata[]

export type UseVideoMetadataInputProps = {
  videoId: string
  load?: boolean
}

export type UseVideoMetadataOutputProps = {
  fetchedPosterUrl?: string
  fetchedSourceUrl?: string
}

const fetchVideoMetadata = (videoId: string) => {
  return fetch(`${videoMetadataUrl}?ids=${[videoId]}`).then((response) =>
    response.json()
  )
}

const getDataFromResponse = (
  response: VideoMetadataResponse,
  videoId: string
): VideoMetadata | undefined => {
  return response.find((p) => p.id === videoId)
}

const geUrlFromSources = (
  sources: VideoMetadata['sources']
): string | undefined => {
  // filter out sources which don't have `https` and `hls`
  return sources?.filter(
    (source) => source.src?.includes('https') && source.src?.includes('hls')
  )[0]?.src
}

const useVideoMetadataFetching = ({
  videoId,
  load = true,
}: UseVideoMetadataInputProps): UseVideoMetadataOutputProps => {
  const [videoMetadata, setVideoMetadata] = useState<
    VideoMetadata | undefined
  >()

  const getVideoMetadata = useCallback(() => {
    fetchVideoMetadata(videoId).then((res) => {
      const response = res as VideoMetadataResponse
      setVideoMetadata(getDataFromResponse(response, videoId))
    })
  }, [videoId])

  useEffect(() => {
    if (videoId && load && !videoMetadata) {
      getVideoMetadata()
    }
  }, [load, videoMetadata, getVideoMetadata, videoId])

  return {
    fetchedPosterUrl: videoMetadata?.poster,
    fetchedSourceUrl: geUrlFromSources(videoMetadata?.sources),
  }
}

export default useVideoMetadataFetching
